import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { FoilCardImage } from 'components/FoilCardImage';
import { Points } from 'components/Points/Points';
import { HaveListRow } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { useGetCardPrintingsQuery } from 'store/services/cards/search/cardsSearchApi';
import { getLanguageDisplayName } from 'constants/languages';
import { allowedConditionsFor } from 'utils/offerConditions';
import {
  constrainToWant,
  filterPrintings,
  finishAvailability,
  pointsForOption,
  PrintingOption,
  toPrintingOptions,
} from './printingOptions';
import './EditHaveListCardModal.css';
import { FinishSegmentedControl } from 'components/FinishSegmentedControl';
import { finishFields, fromHaveFinish, HaveFinish, toHaveFinish } from 'utils/finishes';

/** Same wording as the have-list edit modal's condition dropdown */
const CONDITION_LABELS: Record<string, string> = {
  NEAR_MINT: 'Near Mint (NM)',
  SLIGHTLY_PLAYED: 'Lightly Played (LP)',
  MODERATELY_PLAYED: 'Moderately Played (MP)',
  HEAVILY_PLAYED: 'Heavily Played (HP)',
  DAMAGED: 'Damaged (DMG)',
};

export interface AddUncataloguedSelection {
  cardId: number;
  condition: CardCondition;
  /** Authoritative; `foil` is its derived mirror (finishFields) */
  finish: HaveFinish;
  foil: boolean;
  language: string;
  /**
   * The printing the sender picked. The offer line must render THIS printing,
   * not the want entry's default one — they are frequently different cards
   * (different set, art and collector number) for an any-set want.
   */
  printing: PrintingOption;
}

interface Props {
  isOpen: boolean;
  /** The recipient's want entry this add is answering */
  want: HaveListRow | null;
  onClose: () => void;
  onConfirm: (selection: AddUncataloguedSelection) => Promise<void>;
}

/**
 * "Which copy do you own?" — the single control behind an un-catalogued add
 * from the offer builder's full want-list view.
 *
 * Printing, finish, language and condition are all answers to that one
 * question and are collected together rather than as a sequence of prompts.
 * Nothing here is defaulted silently: condition determines what the sender is
 * PAID, so a silent default would over- or under-pay them without their
 * knowledge, and `UserMTGCardHave` is keyed on cardId — which IS a printing —
 * so there is no have row to create until a printing is named.
 *
 * The printing list is constrained to what the want would actually accept
 * (see constrainToWant); a sender must not be able to pick a printing the
 * recipient's want record would reject.
 */
const AddUncataloguedCardModal: React.FC<Props> = ({ isOpen, want, onClose, onConfirm }) => {
  const [selectedPrintingId, setSelectedPrintingId] = useState<number | null>(null);
  const [selectedFinish, setSelectedFinish] = useState<HaveFinish>('NONFOIL');
  const [selectedCondition, setSelectedCondition] = useState<CardCondition | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const scryfallId = (want?.card as any)?.scryfallId || want?.card?.scryfallID || '';
  const { data: printings, isLoading: isPrintingsLoading } = useGetCardPrintingsQuery(
    { cardId: scryfallId },
    { skip: !isOpen || !scryfallId }
  );

  useEffect(() => {
    if (isOpen) {
      // No silent defaults: printing and condition both start unset so the
      // sender must state which copy they own.
      setSelectedPrintingId(null);
      setSelectedCondition('');
      setSelectedFinish('NONFOIL');
      setSearchTerm('');
      setSaveError(null);
    }
  }, [isOpen, want]);

  // Every printing of this card, then narrowed to what the want accepts.
  const allOptions = useMemo(() => toPrintingOptions(printings), [printings]);
  const acceptableOptions = useMemo(
    () => (want ? constrainToWant(allOptions, want) : allOptions),
    [allOptions, want]
  );

  // The want's accepted languages narrow the list further — a printing row IS
  // a language, so this is a filter on printings rather than a separate control.
  //
  // Two field names on purpose: the want-list controller maps the DB's
  // `acceptedLanguages` to `acceptableLanguages` on the wire, but the PUBLIC
  // want-list endpoint this panel actually calls spreads the raw row and does
  // NOT (PublicCollectionService.ts:895). Reading only the mapped name would
  // silently skip the filter on the very endpoint that feeds this modal.
  const languageConstrained = useMemo(() => {
    const langs =
      want?.acceptableLanguages ?? (want as { acceptedLanguages?: string[] })?.acceptedLanguages;
    if (!Array.isArray(langs) || langs.length === 0) return acceptableOptions;
    const allowed = new Set(langs.map((l) => l.toLowerCase()));
    const narrowed = acceptableOptions.filter((p) => allowed.has((p.lang || 'en').toLowerCase()));
    return narrowed.length > 0 ? narrowed : acceptableOptions;
  }, [acceptableOptions, want]);

  const filteredPrintings = useMemo(
    () => filterPrintings(languageConstrained, searchTerm),
    [languageConstrained, searchTerm]
  );

  const selectedPrinting = useMemo(
    () => languageConstrained.find((p) => p.id === selectedPrintingId),
    [languageConstrained, selectedPrintingId]
  );

  const availableFinishes = useMemo(() => finishAvailability(selectedPrinting), [selectedPrinting]);

  // Auto-correct the finish when the chosen printing does not exist in it — the
  // first finish it does exist in, mirroring the backend's own correction.
  useEffect(() => {
    if (availableFinishes.length > 0 && !availableFinishes.includes(fromHaveFinish(selectedFinish))) {
      setSelectedFinish(toHaveFinish(availableFinishes[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrintingId, availableFinishes]);

  // Conditions the recipient's want record accepts. The row does not exist
  // yet, so the sender's pick IS the row's condition — hence NEAR_MINT as the
  // nominal row condition when intersecting.
  const conditionOptions = useMemo(
    () => allowedConditionsFor(CardCondition.NEAR_MINT, want?.acceptableConditions),
    [want]
  );

  const price = pointsForOption(selectedPrinting, fromHaveFinish(selectedFinish));

  const canSubmit =
    !!selectedPrintingId && !!selectedCondition && !isSaving && conditionOptions.length > 0;

  const handleConfirm = async () => {
    if (!selectedPrintingId || !selectedCondition || !selectedPrinting) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onConfirm({
        cardId: selectedPrintingId,
        condition: selectedCondition as CardCondition,
        ...finishFields(selectedFinish),
        language: selectedPrinting.lang || 'en',
        printing: selectedPrinting,
      });
      onClose();
    } catch (e: any) {
      setSaveError(e?.message || 'Could not add this card. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !want) return null;

  const name = want.cardName || want.card?.name || 'Unknown card';
  const previewScryfallId = selectedPrinting?.scryfallID || want.card?.scryfallID;

  const modalContent = (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h2 className="edit-modal-title">Which copy of {name} do you own?</h2>
          <button className="edit-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="edit-modal-content">
          <div className="edit-card-image-container">
            {previewScryfallId && (
              <FoilCardImage
                key={previewScryfallId}
                src={AssetUrls.cards.getCardImage(previewScryfallId, 'normal')}
                alt={name}
                className="edit-card-image"
                finish={fromHaveFinish(selectedFinish)}
                borderRadius="12px"
                onError={(e) => {
                  e.currentTarget.src = AssetUrls.cards.defaultBack;
                }}
              />
            )}
          </div>

          <div className="edit-card-value-label">
            {price != null ? (
              <Points type="big" points={price} />
            ) : (
              <span className="edit-price-unavailable">
                {selectedPrintingId ? 'Price unavailable' : 'Select a printing'}
              </span>
            )}
          </div>

          {/* Printing */}
          <div className="edit-form-group">
            <label className="edit-label">Printing</label>
            {isPrintingsLoading ? (
              <div className="edit-loading">Loading printings...</div>
            ) : languageConstrained.length === 0 ? (
              <div className="edit-warning">
                No printing of this card matches what {name} is wanted in.
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="edit-input"
                  placeholder="Search by set name, code, or collector #..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                  className="edit-select"
                  value={selectedPrintingId ?? ''}
                  onChange={(e) => setSelectedPrintingId(Number(e.target.value) || null)}
                >
                  <option value="">Select a printing...</option>
                  {filteredPrintings.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.setName} ({p.setCode.toUpperCase()}) #{p.collectorNumber}
                      {p.lang && p.lang !== 'en' ? ` · ${getLanguageDisplayName(p.lang)}` : ''}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          {/* Finish (D-3): single-select; finishes this printing lacks are disabled, never hidden */}
          <div className="edit-form-group">
            <label className="edit-label">Finish</label>
            <FinishSegmentedControl
              value={fromHaveFinish(selectedFinish)}
              available={availableFinishes}
              onChange={(f) => setSelectedFinish(toHaveFinish(f))}
              disabled={isSaving || !selectedPrintingId}
              ariaLabel="Finish"
            />
          </div>

          {/* Condition */}
          <div className="edit-form-group">
            <label className="edit-label">Condition</label>
            <select
              className="edit-select"
              value={selectedCondition}
              onChange={(e) => setSelectedCondition(e.target.value as CardCondition)}
              disabled={conditionOptions.length === 0}
            >
              <option value="">Select a condition...</option>
              {conditionOptions.map((c) => (
                <option key={c} value={c}>
                  {CONDITION_LABELS[c] || c}
                </option>
              ))}
            </select>
            <span className="edit-for-trade-hint">
              Condition sets what you are paid for this card.
            </span>
          </div>

          {/* Privacy statement: this write is not a side effect to discover later */}
          <div className="edit-for-trade-hint">
            This adds the card to your have list so it can be included in this offer. It stays
            private — it will not be listed for trade or shown to other traders.
          </div>

          {saveError && <div className="edit-warning">{saveError}</div>}
        </div>

        <div className="edit-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isSaving}
            fullWidth={false}
          />
          <MagicButton
            label={isSaving ? 'Adding...' : 'Add to offer'}
            color="purple"
            onPress={handleConfirm}
            isDisabled={!canSubmit}
            isLoading={isSaving}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddUncataloguedCardModal;
